<template>
  <div id="SectionCarbonAccountingFAQs">
    <UIContainer class="SectionCarbonAccountingFAQs__container">
      <template #left>
        <SectionTitle>
          <template #title>FAQs</template>
          <template #description>
            Have more questions about Carbon Accounting? Can't find the answer
            you're looking for? Please
            <UIButtonText
              class="SectionCarbonAccountingFAQs__support-link"
              :to="{ name: 'Contact' }"
            >
              contact our support team.
            </UIButtonText>
          </template>
        </SectionTitle>
      </template>

      <template #right>
        <UIFaqs :faqs="faqs" />
      </template>
    </UIContainer>
  </div>
</template>

<script setup lang="ts">
import SectionTitle from '@/views/components/misc/SectionTitle.vue'
import UIButtonText from '@/views/components/ui/buttons/UIButtonText.vue'
import UIContainer from '@/views/components/ui/containers/UIContainer/UIContainer.vue'
import UIFaqs from '@/views/components/features/faqs/UIFaqs.vue'

const faqs = [
  {
    question: 'What is a carbon footprint calculator?',
    answer:
      'A carbon footprint calculator is a digital tool that helps organizations measure and quantify their greenhouse gas (GHG) emissions across their operations. It converts various activities—such as energy consumption, transportation, waste generation, and supply chain operations—into carbon dioxide equivalent (CO2e) values. These calculators use standardized methodologies like the GHG Protocol to ensure accurate and consistent measurements, enabling companies to understand their environmental impact and identify areas for reduction.',
  },
  {
    question:
      "What are the benefits of software to calculate a company's carbon footprint?",
    answer:
      'Carbon footprint calculation software offers numerous benefits including automated data collection and processing, real-time emissions tracking, and comprehensive reporting capabilities. It eliminates manual calculations and spreadsheet errors, saves significant time and resources, ensures compliance with international standards and regulations, and provides actionable insights for emission reduction strategies. Additionally, the software enables businesses to benchmark their performance, track progress over time, and generate audit-ready reports for stakeholders and regulatory bodies.',
  },
  {
    question:
      "How does Sustainalyse's Sustainability Platform help companies measure their emissions?",
    answer:
      'Sustainalyse provides an end-to-end sustainability platform that streamlines the entire emissions measurement process. The platform features intuitive data collection workflows, automated calculations based on the latest emission factors, and comprehensive coverage of all emission scopes. It integrates with existing business systems, offers AI-powered insights for reduction opportunities, and generates detailed reports aligned with international frameworks like GHG Protocol, CDP, and CSRD. Our platform also includes collaborative tools for team management and expert support to ensure accurate and meaningful carbon accounting.',
  },
  {
    question:
      'Does Sustainalyse allow to calculate scope 1, 2, and 3 of carbon emissions?',
    answer:
      'Yes, Sustainalyse enables comprehensive measurement of all three emission scopes. Scope 1 covers direct emissions from owned or controlled sources, Scope 2 includes indirect emissions from purchased energy, and Scope 3 encompasses all other indirect emissions in the value chain—including purchased goods, business travel, employee commuting, waste, and supply chain activities. Our platform provides specialized modules and emission factors for each scope, ensuring complete and accurate carbon accounting across your entire business ecosystem.',
  },
  {
    question:
      'What are the main features of carbon footprint calculation software?',
    answer:
      'Key features of robust carbon footprint calculation software include: automated data collection and integration with business systems; comprehensive emission factor databases that are regularly updated; multi-scope coverage (Scopes 1, 2, and 3); customizable reporting aligned with global standards; data visualization and analytics dashboards; goal setting and progress tracking; scenario modeling for reduction strategies; audit trail and data validation; team collaboration tools; and compliance support for regulations like CSRD, SECR, and CDP reporting requirements.',
  },
  {
    question: 'Why do companies need to calculate their carbon footprint?',
    answer:
      'Companies need to calculate their carbon footprint for multiple strategic and regulatory reasons: meeting increasing regulatory requirements like CSRD and mandatory climate disclosures; responding to investor and stakeholder demands for ESG transparency; identifying cost-saving opportunities through energy efficiency and resource optimization; managing climate-related risks; maintaining competitive advantage as customers prefer sustainable businesses; setting and tracking science-based reduction targets; preparing for carbon pricing mechanisms; and demonstrating corporate responsibility and commitment to climate action. Accurate carbon accounting is the foundation for any credible sustainability strategy.',
  },
]
</script>

<style lang="scss" scoped>
#SectionCarbonAccountingFAQs {
  background-color: $color-white;
  .SectionCarbonAccountingFAQs__container {
    :deep(.UIContainer__grid) {
      display: flex;
      flex-direction: column;
      align-items: stretch;

      @include md {
        flex-direction: row;
        align-items: normal;
      }

      .UIContainer__left {
        width: 100%;
        @include md {
          width: 40%;
          padding-right: 3rem;
        }
      }
      .UIContainer__right {
        width: 100%;
        margin-top: 1rem;
        @include md {
          width: 60%;
          margin-top: 0;
        }
      }
    }
  }

  .SectionCarbonAccountingFAQs__support-link {
    @include font-size-18px();
    color: $color-ui-gray-cool-600;
    text-decoration: underline;
    cursor: pointer;
  }
}
</style>
